import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase'; // <-- import auth here
import { collection, getDocs } from 'firebase/firestore';
import RecordPopup from './RecordPopup';
import SurveyListPopup from './SurveyListPopup';
import './UsersList.css';
import { onAuthStateChanged } from 'firebase/auth';
import { FaSearch, FaUser, FaCalendar, FaPlus, FaEdit } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';


const UsersList = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [user, setUser] = useState(null);
  const [showSurveyList, setShowSurveyList] = useState(false);
  const navigate = useNavigate();
  // Check user on mount
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(firebaseUser => {
      setUser(firebaseUser);
    });

    return () => unsubscribe();
  }, []);


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log("Logged in as:", user.uid);
      } else {
        console.log("Not logged in");
      }
    });
  
    return () => unsubscribe();
  }, []);

  // Fetch documents only if user is logged in
  useEffect(() => {
    console.log('User:', user);
    if (!user) {
      setLoading(false);
      console.log('User is null:', user);

      return;
    }

    const fetchDocuments = async () => {
      try {
        const collectionRef = collection(db, 'EMA');
        const snapshot = await getDocs(collectionRef);
        console.log('Fetched documents:', snapshot.docs.length);
        console.log('Fetched documents:', snapshot.docs);

        console.log('Fetched documents:', snapshot.docs.map(doc => doc.id));
        const docs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(), // includes all fields like createdAt if it exists
        }));
        
        setDocuments(docs);
        setLoading(false);
      } catch (error) {
        if (error.code === 'permission-denied') {
          console.error('You do NOT have permission to read the EMA collection.');
        } else {
          console.error('Error fetching documents:', error.message);
        }
        setError(error.message);
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [user]);

  if (!user) {
    return <div>Please log in to view documents.</div>;
  }

  if (loading) {
    return <div>Loading documents...</div>;
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  // Filter documents based on search term
  const filteredDocuments = documents.filter(doc => 
    doc.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectSurvey = (survey) => {
    setShowSurveyList(false);
    navigate(`/survey-builder?mode=edit&id=${survey.id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-6 flex justify-end space-x-3">
          <button
            onClick={() => navigate('/survey-builder')}
            className="inline-flex items-center px-6 py-2 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-fit"
          >
            <FaPlus className="w-2.5 h-2.5 mr-1" />
            Add Survey
          </button>
          <button
            onClick={() => setShowSurveyList(true)}
            className="inline-flex items-center px-6 py-2 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-fit"
          >
            <FaEdit className="w-2.5 h-2.5 mr-1" />
            Edit Survey
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="relative flex-grow max-w-md">
                <input
                  type="text"
                  placeholder="Search by Document ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700 placeholder-gray-400"
                />
                <FaSearch className="absolute left-3 top-3.5 text-gray-400" />
              </div>
              <div className="text-sm text-gray-500 ml-4 font-medium">
                Showing {filteredDocuments.length} of {documents.length} documents
              </div>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Document ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDocuments.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="px-6 py-8 text-center text-gray-500 text-sm">
                      No documents found matching your search
                    </td>
                  </tr>
                ) : (
                  filteredDocuments.map((doc) => (
                    <tr 
                      key={doc.id} 
                      onClick={() => setSelectedDocument(doc.id)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center">
                              <FaUser className="h-5 w-5 text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{doc.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-left text-sm text-gray-500">{doc.email || '—'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <FaCalendar className="h-4 w-4 mr-2 text-gray-400" />
                          {doc.createdAt?.toDate().toLocaleString()}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedDocument && (
        <RecordPopup
          userId={selectedDocument}
          onClose={() => setSelectedDocument(null)}
        />
      )}

      {showSurveyList && (
        <SurveyListPopup
          onClose={() => setShowSurveyList(false)}
          onSelectSurvey={handleSelectSurvey}
        />
      )}
    </div>
  );
};

export default UsersList;
